/**
 * The network asset a run depicts (spec 2026-10-20-33). Like a room, a run
 * links to an existing asset or gets one created on save: one asset per
 * (level parent, `network_kind`), reused when the parent already has one, so
 * a floor of hundreds of segments saves one asset per network, and saving
 * twice never creates a second one.
 *
 * The level — floor, building or site — picks the parent. The catalogue
 * names the asset type of each network and its default level; nothing here
 * hard-codes a network.
 */
import type { NetworkLevel, PendingAsset, PlanNetwork, RunElement, SceneDoc } from './scene'

export const NETWORK_LEVELS: readonly NetworkLevel[] = ['floor', 'building', 'site']

export interface LevelTarget {
    uid: string
    name: string
}

/** The assets each level files a network under, for the edited frame. */
export type LevelParents = Partial<Record<NetworkLevel, LevelTarget>>

/** A network asset that already exists: what a run can be linked to. */
export interface NetworkAssetRow {
    uid: string
    name: string
    typeCode?: string
    parentUid?: string
    networkKind?: string
}

export interface NetworkContext {
    networks: PlanNetwork[]
    parents: LevelParents
    existing: NetworkAssetRow[]
    /** The network's label in the organization's language. */
    labelOf: (code: string) => string
}

export type RunTarget =
    | { kind: 'existing'; asset: NetworkAssetRow }
    | { kind: 'pending'; pending: PendingAsset }

/** The pending asset key of (level parent, network): one per group, stable across saves. */
export const pendingNetworkKey = (parentUid: string, network: string): string => `network:${parentUid}:${network}`

/**
 * The level actually usable: the one asked for, or the next one up when the
 * frame has no such parent (a plan drawn on a building has no floor).
 */
export function effectiveLevel(level: NetworkLevel | undefined, parents: LevelParents): NetworkLevel | undefined {
    const start = Math.max(0, NETWORK_LEVELS.indexOf(level ?? 'floor'))
    for (let i = start; i < NETWORK_LEVELS.length; i++) {
        if (parents[NETWORK_LEVELS[i]]) return NETWORK_LEVELS[i]
    }
    return undefined
}

/** The catalogue's default level for a network, floor when it names none. */
export function catalogueLevel(network: PlanNetwork | undefined): NetworkLevel {
    const level = network?.default_level
    return level && (NETWORK_LEVELS as readonly string[]).includes(level) ? level : 'floor'
}

/** `Air soufflé – R+1`: the network's label and the level parent's name. */
export const networkAssetName = (label: string, levelName: string): string =>
    levelName.trim() ? `${label} – ${levelName.trim()}` : label

/** A run is linked when it depicts an asset other than the bundle it lives in. */
export const isRunLinked = (run: RunElement): boolean =>
    Boolean(run.owner.assetUid && run.owner.assetUid !== run.source.repOwner?.assetUid)

/**
 * The level a run files its asset at: the one chosen in the editor, else the
 * level of the parent of the asset it is linked to, else the catalogue's.
 */
export function runLevel(run: RunElement, ctx: Pick<NetworkContext, 'networks' | 'parents' | 'existing'>, parentOf?: (uid: string) => string | undefined): NetworkLevel | undefined {
    if (run.level) return effectiveLevel(run.level, ctx.parents)
    if (isRunLinked(run) && run.owner.assetUid) {
        const parentUid = run.owner.assetParentUid
            ?? ctx.existing.find((a) => a.uid === run.owner.assetUid)?.parentUid
            ?? parentOf?.(run.owner.assetUid)
        const level = NETWORK_LEVELS.find((l) => parentUid && ctx.parents[l]?.uid === parentUid)
        if (level) return level
    }
    if (run.owner.newAsset?.parentUid) {
        const level = NETWORK_LEVELS.find((l) => ctx.parents[l]?.uid === run.owner.newAsset?.parentUid)
        if (level) return level
    }
    return effectiveLevel(catalogueLevel(ctx.networks.find((n) => n.code === run.network)), ctx.parents)
}

/**
 * The asset an unlinked run depicts once saved: the level parent's network
 * asset of the same `network_kind` when there is one, else a pending one.
 * Undefined when the catalogue names no type for the network or the frame
 * has no parent at any level.
 */
export function resolveRunTarget(run: RunElement, ctx: NetworkContext): RunTarget | undefined {
    const network = ctx.networks.find((n) => n.code === run.network)
    const typeCode = network?.asset_type
    const level = effectiveLevel(run.level ?? catalogueLevel(network), ctx.parents)
    const parent = level ? ctx.parents[level] : undefined
    if (!typeCode || !parent) return undefined
    const found = ctx.existing.find((a) => a.parentUid === parent.uid && a.networkKind === run.network)
    if (found) return { kind: 'existing', asset: found }
    return {
        kind: 'pending',
        pending: {
            key: pendingNetworkKey(parent.uid, run.network),
            name: networkAssetName(ctx.labelOf(run.network), parent.name),
            typeCode,
            parentUid: parent.uid,
            networkKind: run.network,
        },
    }
}

/**
 * Gives every run that is not linked its network asset: the existing one, or
 * the pending one of its group. `existingOnly` stops at reuse (the load-time
 * pass, which must not make an untouched plan dirty). Returns the number of
 * runs it changed.
 */
export function assignRunNetworkAssets(doc: SceneDoc, ctx: NetworkContext, opts: { existingOnly?: boolean } = {}): number {
    let changed = 0
    for (const el of doc.elements) {
        if (el.kind !== 'run' || isRunLinked(el)) continue
        const target = resolveRunTarget(el, ctx)
        if (!target) continue
        if (target.kind === 'existing') {
            el.owner = {
                assetUid: target.asset.uid,
                assetName: target.asset.name,
                ...(target.asset.typeCode ? { assetTypeCode: target.asset.typeCode } : {}),
                ...(target.asset.parentUid ? { assetParentUid: target.asset.parentUid } : {}),
            }
            changed++
            continue
        }
        if (opts.existingOnly) continue
        if (el.owner.newAsset?.key === target.pending.key) continue
        // A fresh copy per run: the save names and folds each owner on its own.
        el.owner = { newAsset: { ...target.pending } }
        changed++
    }
    return changed
}

/**
 * The level parents of a frame, from the asset index: the frame itself when
 * it is a floor, else its nearest floor ancestor; its building; and the
 * `building_group` at the top of the tree.
 */
export function levelParentsOf(
    frameUid: string,
    lookup: (uid: string) => { uid: string; name?: string; typeCode?: string; parentUid?: string } | undefined,
    isBuilding: (typeCode: string) => boolean = (code) => code === 'building',
): LevelParents {
    const out: LevelParents = {}
    const seen = new Set<string>()
    for (let uid: string | undefined = frameUid; uid && !seen.has(uid) && seen.size < 32;) {
        seen.add(uid)
        const a = lookup(uid)
        if (!a) break
        const target = { uid: a.uid, name: a.name ?? '' }
        if (a.typeCode === 'floor' && !out.floor && !out.building) out.floor = target
        else if (a.typeCode && isBuilding(a.typeCode) && !out.building) out.building = target
        else if (a.typeCode === 'building_group') out.site = target
        uid = a.parentUid
    }
    return out
}

/**
 * What a new floor segment joins on save (spec 2026-10-20-35): the
 * building's network asset of the same `network_kind`, when the building has
 * exactly one. `several` asks the manager to choose once it is saved; `none`
 * leaves it alone. Only a floor-level pending asset is a segment.
 */
export type SegmentAssembly =
    | { kind: 'one'; asset: NetworkAssetRow }
    | { kind: 'several' }
    | { kind: 'none' }

export function segmentAssembly(pending: PendingAsset, ctx: Pick<NetworkContext, 'parents' | 'existing'>): SegmentAssembly | undefined {
    const { floor, building } = ctx.parents
    if (!floor || pending.parentUid !== floor.uid || !pending.networkKind) return undefined
    if (!building) return { kind: 'none' }
    const found = ctx.existing.filter((a) => a.parentUid === building.uid
        && a.networkKind === pending.networkKind
        && (!a.typeCode || a.typeCode === pending.typeCode))
    if (found.length === 1) return { kind: 'one', asset: found[0] }
    return found.length > 1 ? { kind: 'several' } : { kind: 'none' }
}

/**
 * The floor segments a save is about to create, each with the building
 * network it joins once created: only those whose building has exactly one.
 */
export function segmentsToAttach(doc: SceneDoc, ctx: Pick<NetworkContext, 'parents' | 'existing'>): { key: string; assemblyUid: string }[] {
    const out = new Map<string, string>()
    for (const el of doc.elements) {
        const pending = el.kind === 'run' ? el.owner.newAsset : undefined
        if (!pending || out.has(pending.key)) continue
        const target = segmentAssembly(pending, ctx)
        if (target?.kind === 'one') out.set(pending.key, target.asset.uid)
    }
    return [...out].map(([key, assemblyUid]) => ({ key, assemblyUid }))
}
